// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/design-system/components/overlays/Modal.jsx

import React, { useId, useRef } from "react";
import { createPortal } from "react-dom";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";
import useFocusTrap from "../../hooks/useFocusTrap";
import useRestoreFocus from "../../hooks/useRestoreFocus";
import OverlayFooter from "./OverlayFooter";
import OverlayHeader from "./OverlayHeader";

function joinClassNames(...values) {
  return values.filter(Boolean).join(" ");
}

function themeClassName(theme) {
  if (theme === "dark") return "oasys-theme-dark";
  if (theme === "light") return "oasys-theme-light";
  return "";
}

export default function Modal({
  open = true,
  eyebrow,
  title,
  description,
  children,
  actions = [],
  footerNote = null,
  onClose,
  closeOnBackdrop = true,
  closeOnEscape = true,
  size = "large",
  theme = "inherit",
  className = "",
  overlayClassName = "",
  headerClassName = "",
  bodyClassName = "",
  initialFocusRef = null,
  ariaLabel,
  maxWidth,
  accentBorder,
  zIndex,
}) {
  const dialogRef = useRef(null);
  const reactId = useId();
  const titleId = `${reactId}-title`;
  const descriptionId = description ? `${reactId}-description` : undefined;

  useBodyScrollLock(open);
  useRestoreFocus(open);
  useFocusTrap(dialogRef, open, initialFocusRef);

  if (!open || typeof document === "undefined") return null;

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && closeOnEscape && onClose) {
      event.stopPropagation();
      onClose();
    }
  };

  const handleBackdropMouseDown = (event) => {
    if (
      closeOnBackdrop &&
      onClose &&
      event.target === event.currentTarget
    ) {
      onClose();
    }
  };

  const overlayStyle = zIndex ? { zIndex } : undefined;
  const dialogStyle = {
    ...(maxWidth ? { maxWidth } : null),
    ...(accentBorder ? { borderColor: accentBorder } : null),
  };

  const modal = (
    <div
      className={joinClassNames(
        "oasys-overlay-backdrop",
        themeClassName(theme),
        overlayClassName
      )}
      style={overlayStyle}
      onMouseDown={handleBackdropMouseDown}
      onKeyDown={handleKeyDown}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={!ariaLabel ? titleId : undefined}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={joinClassNames(
          "oasys-modal",
          `oasys-modal--${size}`,
          className
        )}
        style={dialogStyle}
      >
        <OverlayHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
          onClose={onClose}
          titleId={titleId}
          descriptionId={descriptionId}
          className={headerClassName}
        />

        <div
          className={joinClassNames(
            "oasys-overlay-body",
            bodyClassName
          )}
        >
          {children}
        </div>

        <OverlayFooter actions={actions} note={footerNote} />
      </section>
    </div>
  );

  return createPortal(modal, document.body);
}
